import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { LogIn, Menu } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { CommandPalette } from "../components/CommandPalette";
import { LoginDialog } from "../components/LoginDialog";
import { HilfePanel, HilfeProvider } from "../features/hilfe";
import { SeitenFehler } from "../components/SeitenFehler";
import { merkeOrt } from "../lib/letzterOrt";
import { sidebarItems, sidebarFooterItems, sichtbareEintraege } from "../config/sidebar";
import { useAuthed, useIch } from "../lib/useAuthed";
import { darf } from "../api";
import { farbStil } from "../config/bereichsfarben";

const COLLAPSED_KEY = "home.sidebar.collapsed";

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
}

// Flattened sidebar items in their visual order — used by H/L vim
// navigation to step through the same rows the user sees on screen.
// Footer items come last so j keeps descending past the divider.
const VIM_NAV_ITEMS = [...sidebarItems, ...sidebarFooterItems];

type VimFocus = "main" | "sidebar";

export function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [hilfeOpen, setHilfeOpen] = useState(false);
  /** Zu welcher Seite. Leer heisst „die, auf der ich stehe" — so öffnet der
   *  Eintrag in der Leiste weiterhin die Hilfe zur aktuellen Seite. */
  const [hilfePfad, setHilfePfad] = useState<string | null>(null);
  const authed = useAuthed();
  // Ohne Anmeldung stehen nur die öffentlichen Einträge in der Leiste — die
  // Tastaturnavigation muss dieselbe Liste durchlaufen, sonst springt sie auf
  // Zeilen, die niemand sieht.
  const ich = useIch();
  const navItems = useMemo(
    () => sichtbareEintraege(VIM_NAV_ITEMS, authed, darf),
    [authed, ich],
  );
  // Vim-mode focus area + cursor inside the sidebar. `null` index means
  // "no row highlighted yet" — first j/k press selects the current route
  // so the user always picks up from where they are.
  const [vimFocus, setVimFocus] = useState<VimFocus>("main");
  const [sidebarVimIndex, setSidebarVimIndex] = useState<number | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const loc = useLocation();
  const navigate = useNavigate();
  const close = () => setDrawerOpen(false);

  // Persist collapse state across reloads.
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  // Close mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Wo man in einem Bereich zuletzt stand, damit die Leiste dorthin
  // zurueckfuehrt statt an den Anfang. Siehe lib/letzterOrt.
  useEffect(() => { merkeOrt(loc.pathname, loc.search); }, [loc.pathname, loc.search]);

  // Global vim-style keyboard layer. Bindings only fire when the user is
  // *not* typing — any input/textarea/contentEditable target short-circuits
  // the whole handler so search fields keep working normally.
  //
  //   i / /        → open command palette (also Ctrl/⌘+K, works in inputs)
  //   Shift+H      → focus the sidebar (move the vim cursor onto the nav)
  //   Shift+L      → focus the main column (default; scroll mode)
  //   sidebar:     j/k step rows, Enter activates, Esc returns to main,
  //                h/l also leave the sidebar (l = into main, h = unchanged)
  //   main:        j/k scroll vertically, h/l scroll horizontally
  useEffect(() => {
    const SCROLL_STEP = 80;
    const onKey = (e: KeyboardEvent) => {
      // Solange Palette oder Hilfe offen sind, gehört die Tastatur ihnen.
      // Beide schließen mit Esc selbst — j/k dürfen hier nicht die Seite
      // dahinter verschieben.
      if (paletteOpen || hilfeOpen) return;
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);

      // ⌘K / Ctrl+K — works everywhere, including inside inputs.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (typing) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // `?` (Umschalt + /) schlägt die Hilfe zur aktuellen Seite auf. Steht
      // vor dem Suchauslöser, weil `/` und `?` zwar dieselbe Taste sind,
      // aber verschiedene `key`-Werte liefern — verwechseln kann man sie
      // nicht, nur übersehen.
      if (e.key === "?") {
        e.preventDefault();
        // Nur angemeldet: der Text kommt vom Server und nur gegen Anmeldung
        // (siehe features/hilfe/inhalte.ts). Ein Fenster, das dann leer
        // bliebe, ist schlimmer als keines.
        if (authed) setHilfeOpen(true);
        return;
      }

      // Vim-style search trigger.
      if (e.key === "i" || e.key === "/") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }

      // Shift+H / Shift+L — switch the vim focus area. Capital letters
      // arrive as `key === "H"` already, so checking the key directly is
      // enough (no need to test shiftKey explicitly).
      if (e.key === "H") {
        e.preventDefault();
        setVimFocus("sidebar");
        // Seed the cursor onto the current route so j/k starts where the
        // user already is, rather than snapping to the top of the nav.
        setSidebarVimIndex((prev) => {
          if (prev != null) return prev;
          const active = navItems.findIndex((it) =>
            it.path === "/" ? loc.pathname === "/" : loc.pathname.startsWith(it.path),
          );
          return active >= 0 ? active : 0;
        });
        return;
      }
      if (e.key === "L") {
        e.preventDefault();
        setVimFocus("main");
        return;
      }

      if (vimFocus === "sidebar") {
        if (e.key === "j") {
          e.preventDefault();
          setSidebarVimIndex((i) => Math.min(navItems.length - 1, (i ?? -1) + 1));
        } else if (e.key === "k") {
          e.preventDefault();
          setSidebarVimIndex((i) => Math.max(0, (i ?? 1) - 1));
        } else if (e.key === "Enter") {
          e.preventDefault();
          const it = navItems[sidebarVimIndex ?? -1] ?? null;
          if (it) navigate(it.path);
        } else if (e.key === "Escape" || e.key === "l") {
          e.preventDefault();
          setVimFocus("main");
        }
        return;
      }

      // main focus → directional scrolling. `.main` is the actual
      // overflow:auto container (the page itself doesn't scroll), so the
      // ref always points at the right element to nudge.
      const el = mainRef.current;
      if (!el) return;
      const scroll = (dx: number, dy: number) => {
        el.scrollBy({ left: dx, top: dy, behavior: "smooth" });
      };
      if (e.key === "j")      { e.preventDefault(); scroll(0,  SCROLL_STEP); }
      else if (e.key === "k") { e.preventDefault(); scroll(0, -SCROLL_STEP); }
      else if (e.key === "h") { e.preventDefault(); scroll(-SCROLL_STEP, 0); }
      else if (e.key === "l") { e.preventDefault(); scroll( SCROLL_STEP, 0); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen, hilfeOpen, vimFocus, sidebarVimIndex, loc.pathname, navigate, navItems]);

  // Der Ton des Bereichs, in dem man gerade steht. Er kommt aus derselben
  // Quelle wie die Farbe in der Seitenleiste und wie die Kachel auf dem
  // Dashboard, die hierher führt — siehe config/bereichsfarben.
  const mainStyle: CSSProperties = farbStil(loc.pathname);

  // Dieselben zwei Marken zusätzlich an der Wurzel.
  //
  // Fenster hängen in einem Portal am `body` und damit AUSSERHALB von
  // `main`. Sie erbten den Ton des Bereichs deshalb nicht: das
  // Schreibfenster der Mailseite trug einen mauvefarbenen Fokusring und
  // einen mauvefarbenen „Send"-Knopf, während die Seite dahinter
  // durchgehend pfirsich war. Am `html` steht der Ton für alles, auch für
  // das, was neben dem Baum hängt.
  useEffect(() => {
    const w = document.documentElement;
    for (const [k, v] of Object.entries(farbStil(loc.pathname))) {
      w.style.setProperty(k, String(v));
    }
  }, [loc.pathname]);

  return (
    <div className={`layout${collapsed ? " collapsed" : ""} vim-focus-${vimFocus}${authed ? "" : " has-topbar-login"}`}>
      <aside className={`sidebar${drawerOpen ? " open" : ""}`}>
        <Sidebar
          onNavigate={close}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((v) => !v)}
          vimFocusedIndex={vimFocus === "sidebar" ? sidebarVimIndex : null}
          onLogin={() => setLoginOpen(true)}
          onHilfe={() => setHilfeOpen(true)}
        />
      </aside>
      {drawerOpen && <div className="backdrop" onClick={close} />}

      <button
        className="mobile-hamburger"
        aria-label="Menu"
        onClick={() => setDrawerOpen((o) => !o)}
      >
        <Menu className="icon-svg" />
      </button>

      {/* Oben rechts nur noch der Weg hinein — bewusst kein Link auf /login:
          der Dialog lässt den Besucher auf der Seite stehen, auf der er
          gerade ist. Die Hilfe stand hier ebenfalls, als runder Knopf über
          allem, und schob dafür jeden Seitenkopf nach unten. Sie steht jetzt
          unten in der Leiste neben den Meldungen und den Einstellungen. */}
      {!authed && (
        <button className="topbar-login" onClick={() => setLoginOpen(true)}>
          <LogIn className="icon-svg" />
          <span>Sign in</span>
        </button>
      )}

      <main className="main" style={mainStyle} ref={mainRef}>
        {/* Re-keying on pathname remounts the wrapper, which re-fires the CSS
            enter animation. Keeps transitions a pure-CSS detail without
            pulling in a transition library. */}
        {/* Die Überschrift jeder Seite kann die Hilfe zu ihr aufschlagen —
            dafür reicht sie hier durch, statt als Parameter durch dreißig
            Seiten zu wandern. Siehe features/hilfe/HilfeKontext. */}
        <HilfeProvider oeffnen={(pfad) => { setHilfePfad(pfad ?? null); setHilfeOpen(true); }}>
          {/* Die Auffangstelle sitzt *innerhalb* des Rahmens: stolpert eine
              Seite, bleibt die Leiste stehen und man klickt woanders hin.
              Der Schluessel ist derselbe wie beim Rahmen — ein Wechsel baut
              beides neu auf, und damit ist der Fehler vergessen. */}
          <div className="page-transition" key={loc.pathname}>
            <SeitenFehler>
              <Outlet />
            </SeitenFehler>
          </div>
        </HilfeProvider>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      <HilfePanel
        open={hilfeOpen}
        onClose={() => setHilfeOpen(false)}
        pfad={hilfePfad ?? loc.pathname}
      />
    </div>
  );
}
